import jsPDF from 'jspdf';
import Logo from "../images/shop_logo.png";
import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from '../Variables.js';

const generateReceiptPDF = (cart, user, cashierFirstName, cashierLastName, customerName, customerPhone, saleTimestamp) => {
  if (cart.length === 0) {
    throw new Error("No items in cart to generate receipt");
  }

  // Convert Firestore Timestamp to JS Date if needed
  const saleDate = saleTimestamp.toDate ? saleTimestamp.toDate() : saleTimestamp;

  // Format date: "15 Dec 2025"
  const formattedDate = saleDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // Format time: "10:23:45 PM" (with seconds)
  const formattedTime = saleDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  // Combine: "15 Dec 2025, 10:23:45 PM"
  const formattedSaleDate = `${formattedDate}, ${formattedTime}`;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [80, 297]
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 5;
  const contentWidth = pageWidth - (2 * margin);
  let yPos = margin;

  try {
    const imgWidth = 20;
    const imgHeight = 20;
    const centerX = (pageWidth - imgWidth) / 2;
    doc.addImage(Logo, 'PNG', centerX, yPos, imgWidth, imgHeight);
    yPos += 25;
  } catch (err) {
    console.log("Logo not found, continuing without it");
  }

  // School Name with wrapping
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  
  // Split school name if too long
  const maxNameWidth = contentWidth - 2; // Slightly less than full width for padding
  const schoolNameLines = doc.splitTextToSize(SHOP_NAME.toUpperCase(), maxNameWidth);
  
  schoolNameLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  });

  // School Address with wrapping
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  // Address wrapping & centering
  const addressMaxWidth = contentWidth - 4; // Even more padding for address
  const wrappedAddress = doc.splitTextToSize(SHOP_ADDRESS, addressMaxWidth);
  
  // Add a small gap before address
  yPos += 2;
  
  wrappedAddress.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  });

  // Phone number rendered after the address
  doc.text(`Tel: ${SHOP_PHONE}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 8; // Increased gap after phone

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SALES RECEIPT', pageWidth / 2, yPos, { align: 'center' });
  yPos += 2;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const cashierName = `${cashierFirstName} ${cashierLastName}`.trim() || user.email || 'Unknown';

  const details = [
    `Date: ${formattedSaleDate}`,
    `Receipt No: ${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`,
    `Cashier: ${cashierName}`,
    ...(customerName ? [`Customer: ${customerName}`] : []),
    ...(customerPhone ? [`Phone: ${customerPhone}`] : [])
  ];

  details.forEach(text => {
    doc.text(text, margin, yPos);
    yPos += 5;
  });

  yPos += 5;

  doc.setFont('helvetica', 'bold');
  doc.text('Item', margin, yPos);
  doc.text('Qty', margin + 38, yPos);
  doc.text('Amount', margin + 50, yPos);
  yPos += 2;

  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  cart.forEach((item) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = margin;
    }

    doc.setFontSize(8);
    
    // Item name with price in parentheses
    const itemDisplay = `${item.name} (N${item.price.toLocaleString()})`;
    const itemMaxWidth = 35; // Width for item name column
    
    // Split item name if too long
    const nameLines = doc.splitTextToSize(itemDisplay, itemMaxWidth);
    
    nameLines.forEach((line, i) => {
      doc.text(line, margin, yPos + (i * 4));
    });
    
    // Calculate vertical position based on number of lines
    const itemHeight = Math.max(8, nameLines.length * 4);
    
    // Quantity and subtotal aligned properly
    doc.text(`${item.quantity}`, margin + 40, yPos + (itemHeight - 4) / 2);
    doc.text(`N ${item.subtotal.toLocaleString()}`, margin + 48, yPos + (itemHeight - 4) / 2);

    yPos += itemHeight;
  });

  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.subtotal, 0);
  };

  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', margin, yPos);
  doc.text(`N ${calculateTotal().toLocaleString()}`, margin + 42, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  
  // Wrap thank you message if needed
  const thankYouMessage = 'Thank you for your patronage!';
  const thankYouLines = doc.splitTextToSize(thankYouMessage, contentWidth - 10);
  
  thankYouLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPos + (index * 4), { align: 'center' });
  });
  
  yPos += thankYouLines.length * 4 + 5;

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  
  // Wrap generated date if needed
  const generatedText = `Generated on ${new Date().toLocaleString()}`;
  const generatedLines = doc.splitTextToSize(generatedText, contentWidth - 10);
  
  generatedLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPos + (index * 4), { align: 'center' });
  });
  
  yPos += generatedLines.length * 4 + 5;

  // Adjust the PDF height to content if needed
  const contentHeight = yPos + 10;
  if (contentHeight > 297) {
    doc.internal.pageSize.height = contentHeight;
  }

  return doc;
};

export default generateReceiptPDF;